import type { AbsenceCode, CalcPass, CalcResponse, Cell, CellHistoryItem } from '@tabel/shared/schemas/cells';
import type { EngineDayCalculated } from '@tabel/shared/schemas/day-trace';
import type { AnswerResponse, Question } from '@tabel/shared/schemas/questions';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Bot,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock,
  Coffee,
  FileText,
  Info,
  Link2,
  MessageCircleQuestion,
  MessagesSquare,
  MoreHorizontal,
  PenLine,
  RotateCcw,
  Send,
  Table2,
  TriangleAlert,
  Users,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { type CellBase, api } from '../lib/api';
import { ApiError } from '../lib/errors';
import {
  type HoursFormat,
  STATE_LABEL,
  brigadeList,
  engineNote,
  hours,
  hoursText,
  ruDate,
  ruDateTime,
  shownHours,
  weekday,
} from '../lib/format';
import {
  answerOptions,
  clockSeconds,
  compareQuestions,
  isAnswered,
  isUnanswered,
  nq,
  reviewable,
  toReview,
  typeKind,
  typeLabel,
} from '../lib/questions';
import {
  RECALC_LABEL,
  RECALC_TEXT,
  SIGNED_NOTE_TEXT,
  recalcsOf,
  sayRecalc,
  signedNotesOf,
  staleEditOf,
} from '../lib/recalc';
import type { AppRole } from '../lib/role';
import { CellEditFooter, CellEditForm, baseOf, useCellEdit } from './CellEditForm';
import { AGENT_LINK_LABEL, useDayAgentLink } from './DayCardAgentLink';
import { DayPasses, type StatedSpan } from './DayPasses';
import {
  ABSENCE_NAME,
  EngineDayTrace,
  EngineNotCalculated,
  EngineReceipt,
  absenceSources,
  sourceText,
} from './EngineDay';
import { type ToastAction, errorText, useToast } from './ErrorToast';
import { HistoryList } from './HistoryList';
import {
  type AnswerDraftApi,
  AnswerFooter,
  QuestionBlock,
  ReviewFooter,
  type StripTone,
  questionStrip,
  useAnswerDraft,
  useAnswerKeys,
  useReview,
} from './QuestionBlock';
import { useUnsavedGuard } from './UnsavedGuard';
import { useAssistant } from './assistant/AssistantPanel';
import { LABEL as ASSISTANT_LABEL } from './assistant/texts';
import { Badge, Button, Callout, Dropdown, IconButton, MenuItem, Segmented, SidePanel } from './ui';

/*
 * Карточка дня (003, US1; FR-001–FR-011; contracts/ui-panel.md, «Карточка дня: состав», «Подвал»): одна правая панель
 * для любой прошедшей и сегодняшней ячейки вместо окон «Расчёт дня» и «Правка часов». Содержимое окна дня перенесено
 * без переделки: чек расчёта, полоса и таблица проходов, «Подробно для разработчика», история из всех табелей
 * человека. Новое — полоса состояния, свойства, «Отметки» (сохраняются сразу), подвал с правкой и меню «…».
 * Часы считает сервер (движок): карточка только показывает значения.
 */

const SCREEN = 'Timesheet';

/** Пометки проходов 001 (табель без движка). */
const MARK: Record<CalcPass['mark'], string> = {
  used: 'учтён',
  merged: 'склеен (повтор)',
  false_pair: 'ложная пара',
  outside: 'вне пары',
  no_direction: 'неизвестный терминал',
};

/** «Нет выхода», «нет входа», «больше 24 ч» и ошибка расчёта — проблемы: красным (дизайн-система, «Цвет»). */
const PROBLEM = new Set<Cell['machineState']>(['no_exit', 'no_entry', 'over_24h', 'error']);

/** Машинных часов нет — почему, словами. */
const NO_MACHINE: Partial<Record<Cell['machineState'], string>> = {
  no_exit: 'нет — нет выхода',
  no_entry: 'нет — нет входа',
  over_24h: 'нет — за дату больше 24 ч',
  error: 'нет — ошибка расчёта',
  on_site: 'нет — день ещё идёт',
  empty: 'нет — проходов нет',
};

/**
 * Действия с ячейкой по правам роли и статусу (раздел «Права ролей»). Нет функции — действия нет. Правка бросает
 * ошибку — форма остаётся открытой; «в обед», код и отмена правки сохраняются сразу, ошибку показывает страница.
 */
export interface DayActions {
  edit?: (hours: number, comment: string, base: CellBase) => Promise<unknown>;
  cancelEdit?: (base: CellBase) => Promise<unknown>;
  lunch?: (value: boolean) => Promise<unknown>;
  absence?: (code: AbsenceCode | null) => Promise<unknown>;
  /** «Правка устарела» (004, FR-044): «Оставить правку» — `keep`, «Принять расчёт» — `accept`; кадры в «Завершён». */
  editDecision?: (decision: 'keep' | 'accept') => Promise<unknown>;
}

type AbsenceValue = 'none' | AbsenceCode;
const ABSENCE_OPTIONS = [
  { value: 'none', label: 'Нет', title: 'Без кода' },
  { value: 'О', label: 'О', title: 'Отпуск' },
  { value: 'Б', label: 'Б', title: 'Больничный' },
  { value: 'ДО', label: 'ДО', title: 'Без содержания' },
] as const satisfies readonly { value: AbsenceValue; label: string; title: string }[];

/** Документ дня: код в этой ячейке или документ из другого табеля человека (строка чека «Документ»). */
function documentOf(
  cell: Cell,
  engine: EngineDayCalculated | null,
): { code: string; external: boolean } | null {
  // `absenceOther` — код поставлен в табеле другой бригады человека (T094): документ дня и здесь.
  if (cell.absenceCode) return { code: cell.absenceCode, external: Boolean(cell.absenceOther) };
  const row = engine?.receipt.rows.find((r) => r.kind === 'документ');
  return row ? { code: row.value, external: true } : null;
}

/** Последняя правка этого табеля в истории дня — когда и кто. */
const DAY_SEC = 86_400;

/**
 * Отрезок «по вашему ответу» для полосы дня (T071, Q-27). Пока бригадир отвечает временем — по расчёту сервера
 * (`span` предпросмотра); записанный ответ временем — время бригадира от открытого прохода дня. Иначе — нет.
 * Это только раскладка на полосе: часы по ответу считает сервер.
 */
function statedOf(
  q: Question | null,
  draft: AnswerDraftApi | null,
  date: string,
  tzOffset: string,
): StatedSpan | null {
  if (!q) return null;
  if (draft?.active && draft.question.id === q.id) {
    if (draft.values.option !== 'time' || draft.preview.status !== 'ok') return null;
    const r = draft.preview.results.find((x) => x.questionId === q.id);
    if (!r?.ok || !r.span) return null;
    const from = clockSeconds(r.span.from);
    const to = clockSeconds(r.span.to);
    // Выход раньше входа по часам — выход следующих суток (сервер это отмечает `nextDay`).
    const nextDay = Boolean(r.span.nextDay) || to <= from;
    return { from, to: nextDay ? to + DAY_SEC : to, nextDay };
  }
  const a = q.answer;
  if (a?.option !== 'time' || !a.statedAt || !a.statedKind) return null;
  const local = ruDateTime(a.statedAt, tzOffset); // «дд.мм.гггг чч:мм»
  const sec = clockSeconds(local.slice(11, 16));
  if (a.statedKind === 'вход') return { from: sec, to: null, nextDay: false };
  const nextDay = local.slice(0, 10) !== ruDate(date);
  return { from: null, to: nextDay ? sec + DAY_SEC : sec, nextDay };
}

/** Почему система сняла вопрос — для строки расхождения. «Пришёл проход» — у «нет выхода» и «нет входа». */
function removedWhy(q: Question): string {
  switch (q.removedReason) {
    case 'flag_gone':
      return ['нет_выхода', 'нет_входа'].includes(typeKind(q.type))
        ? 'пришёл проход'
        : 'пометка исчезла после пересчёта';
    case 'absence':
      return 'на день поставлен документ';
    case 'row_removed':
      return 'человека убрали из табеля';
    default:
      return 'день больше не подходит для вопроса';
  }
}

/**
 * Расхождение после снятия вопроса пересчётом (T109; FR-020, US2.12): «Вопрос «Нет выхода» снят: пришёл проход. По
 * проходам 8 ч, по ответу 10 ч (выход в 19:00, указано бригадиром). В табеле осталась правка бригадира.» Часы по
 * проходам — факты дня сейчас, по ответу — часы ответа бригадира: оба числа считает сервер. Ответа с часами нет —
 * расхождения нет (`null`). Только для вопроса, снятого пересчётом, пока правка бригадира в табеле (`answerInTable`).
 */
function removedNote(
  q: Question,
  o: { format: HoursFormat | null; tzOffset: string; date: string },
): string | null {
  const a = q.answer;
  if (a?.hours == null) return null;
  const machine =
    q.facts.machineHours == null
      ? 'по проходам часов нет'
      : `по проходам ${shownHours(q.facts.machineValue, q.facts.machineHours)} ч`;
  let time = '';
  if (a.option === 'time' && a.statedAt) {
    const local = ruDateTime(a.statedAt, o.tzOffset);
    const day = local.slice(0, 10) !== ruDate(o.date) ? ` ${local.slice(0, 5)}` : '';
    time = ` (${a.statedKind === 'вход' ? 'вход' : 'выход'}${day} в ${local.slice(11)}, указано бригадиром)`;
  }
  return [
    `Вопрос «${typeLabel(q.type)}» снят: ${removedWhy(q)}.`,
    ` ${machine.charAt(0).toUpperCase()}${machine.slice(1)}, по ответу ${hoursText(a.hours, o.format)} ч${time}.`,
    ' В табеле осталась правка бригадира.',
  ].join('');
}

function lastEditOf(history: readonly CellHistoryItem[], timesheetId: number): CellHistoryItem | undefined {
  return [...history].reverse().find((h) => h.type === 'cell.edit' && h.timesheetId === timesheetId);
}

const stamp = (iso: string, tzOffset: string) => {
  const full = ruDateTime(iso, tzOffset);
  return `${full.slice(0, 5)} ${full.slice(11)}`;
};

const STRIP_TONE: Record<StripTone, string> = {
  neutral: 'bg-subtle text-ink-2',
  warn: 'bg-warn-soft text-warn',
  bad: 'bg-bad-soft text-bad',
  ok: 'bg-ok-soft text-ok',
};

/** Полоса состояния под шапкой панели. Без вопросов: документ, правка, «на объекте». */
function StatusStrip({
  tone,
  icon: Icon,
  children,
}: { tone: StripTone; icon: typeof Info; children: ReactNode }) {
  return (
    <div
      data-day-status
      className={`flex shrink-0 items-center gap-2 border-b border-line px-6 py-2 text-[13px] max-sm:px-4 ${STRIP_TONE[tone]}`}
    >
      <Icon size={14} strokeWidth={1.75} className="shrink-0" aria-hidden />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

/** Значение «В табеле» — как в ячейке: часы, код, состояние дня. */
function CellPill({ cell }: { cell: Cell }) {
  const base =
    'inline-flex h-6 items-center gap-1.5 rounded-control border px-2 text-[13px] font-medium tabular-nums';
  if (cell.absenceCode) {
    return (
      <span className={`${base} border-line`}>
        <b className="font-semibold">{cell.absenceCode}</b>
        <span className="font-normal text-ink-2">{ABSENCE_NAME[cell.absenceCode] ?? ''}</span>
      </span>
    );
  }
  if (cell.finalHours != null) {
    const manual = cell.manualHours != null;
    return (
      <span className={`${base} ${manual ? 'border-transparent bg-edit' : 'border-line'}`}>
        {shownHours(cell.finalValue, cell.finalHours)} ч
        {manual && (
          <span className="text-warn" aria-label="изменено вручную">
            ✎
          </span>
        )}
      </span>
    );
  }
  if (cell.machineState === 'on_site') {
    return <span className={`${base} border-line font-normal text-ink-3 italic`}>на объекте</span>;
  }
  if (PROBLEM.has(cell.machineState)) {
    return (
      <span className={`${base} border-line`}>
        <span className="font-semibold text-bad">{STATE_LABEL[cell.machineState]}</span>
        <span className="font-normal text-ink-2">часов нет</span>
      </span>
    );
  }
  return <span className="text-ink-3">—</span>;
}

function Prop({ icon: Icon, label, children }: { icon: typeof Info; label: string; children: ReactNode }) {
  return (
    <>
      <dt className="flex min-h-8 items-center gap-2 text-ink-2">
        <Icon size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
        {label}
      </dt>
      <dd className="flex min-h-8 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 py-1">{children}</dd>
    </>
  );
}

const Faint = ({ children }: { children: ReactNode }) => (
  <span className="text-[12.5px] text-ink-3">{children}</span>
);

/** Свойства дня (contracts/ui-panel.md, «Свойства»): в табеле, документ, машинные часы, правка, обед, бригады. */
function Props({
  cell,
  doc,
  engine,
  history,
  timesheetId,
  tzOffset,
  format,
  calc,
  questions,
}: {
  cell: Cell;
  doc: { code: string; external: boolean } | null;
  engine: EngineDayCalculated | null;
  history: readonly CellHistoryItem[];
  timesheetId: number;
  tzOffset: string;
  format: HoursFormat | null;
  calc: CalcResponse | undefined;
  /** Вопросы дня (003). */
  questions: readonly Question[];
}) {
  const open = questions.filter(isUnanswered).length;
  const lunchRow = engine?.receipt.rows.find((r) => r.kind === 'обед');
  const sources = doc
    ? absenceSources(history, doc.code).map((h) => sourceText(h, timesheetId, tzOffset))
    : [];
  // При документе часы на объекте в табель не идут: время — как в чеке (строка «На объекте» при документе), иначе
  // по проходам; табель 001 — часы по СКУД.
  const present = engine
    ? (engine.receipt.rows.find((r) => r.kind === 'присутствие')?.value ?? engine.day.totals.present)
    : calc?.hours != null
      ? `${hours(calc.hours)} ч`
      : null;
  const edit = cell.manualHours != null ? lastEditOf(history, timesheetId) : undefined;
  return (
    <dl className="grid grid-cols-[136px_minmax(0,1fr)] gap-y-0.5 text-[13px] max-sm:grid-cols-[112px_minmax(0,1fr)]">
      <Prop icon={Table2} label="В табеле">
        <CellPill cell={cell} />
        {doc && <Faint>документ на весь день</Faint>}
      </Prop>
      {doc && (
        <Prop icon={FileText} label="Документ">
          <span className="flex flex-col">
            <span>на весь день: {ABSENCE_NAME[doc.code] ?? doc.code}</span>
            {sources.map((s) => (
              <Faint key={s}>{s}</Faint>
            ))}
          </span>
        </Prop>
      )}
      {doc ? (
        <Prop icon={Clock} label="На объекте">
          {present ? (
            <>
              <span className="tabular-nums">{present}</span>
              <Faint>эти часы в табель не идут</Faint>
            </>
          ) : (
            <span className="text-ink-3">проходов нет</span>
          )}
        </Prop>
      ) : (
        <Prop icon={Clock} label="Машинные часы">
          {cell.machineHours != null ? (
            <span className="tabular-nums">{shownHours(cell.machineValue, cell.machineHours)} ч</span>
          ) : (
            <span className={PROBLEM.has(cell.machineState) ? 'text-bad' : 'text-ink-3'}>
              {NO_MACHINE[cell.machineState] ?? 'нет'}
            </span>
          )}
        </Prop>
      )}
      {cell.manualHours != null && (
        <Prop icon={PenLine} label="Правка">
          <span className="tabular-nums">{hoursText(cell.manualHours, format)} ч</span>
          <Faint>
            {[
              cell.manualComment ? `«${cell.manualComment}»` : null,
              cell.manualByRole === 'hr' ? 'кадры' : 'бригадир',
              edit?.author,
              edit ? stamp(edit.ts, tzOffset) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Faint>
        </Prop>
      )}
      {!doc && (cell.lunch || lunchRow) && (
        <Prop icon={Coffee} label="Обед">
          {cell.lunch ? (
            <span>работал в обед — отметка бригадира</span>
          ) : (
            <>
              <span className="tabular-nums">{lunchRow?.value}</span>
              {lunchRow?.note && <Faint>{lunchRow.note}</Faint>}
            </>
          )}
        </Prop>
      )}
      {cell.multiBrigade?.length ? (
        <Prop icon={Users} label="Другая бригада">
          <span>{brigadeList(cell.multiBrigade)}</span>
          <Faint>есть часы или проходы и там — проверьте, в какой бригаде был этот день</Faint>
        </Prop>
      ) : null}
      {questions.length > 0 && (
        <Prop icon={MessageCircleQuestion} label="Вопросы дня">
          <span>{nq(questions.length)}</span>
          {open > 0 && <Faint>без ответа {open}</Faint>}
        </Prop>
      )}
    </dl>
  );
}

/** Табель без движка (001): часы по СКУД и в табеле, таблица проходов с пометками. */
function Calc001({ calc, cell }: { calc: CalcResponse; cell: Cell }) {
  const final =
    cell.absenceCode ??
    cell.finalValue ??
    (cell.finalHours != null ? `${hours(cell.finalHours)} ч` : STATE_LABEL[cell.machineState] || '—');
  return (
    <>
      <section aria-label="Проходы за день">
        <h3 className="mb-2 text-[14px] font-semibold text-ink">Проходы за день</h3>
        <div className="overflow-hidden rounded-control border border-line">
          <table className="w-full table-fixed text-[13px]">
            <thead className="bg-subtle text-left text-[12px] text-ink-3">
              <tr>
                <th className="w-16 px-2.5 py-2 font-medium">Время</th>
                <th className="px-2.5 py-2 font-medium">Терминал</th>
                <th className="w-[22%] px-2.5 py-2 font-medium">Направление</th>
                <th className="w-[28%] px-2.5 py-2 font-medium">Пометка</th>
              </tr>
            </thead>
            <tbody>
              {calc.passes.length === 0 && (
                <tr>
                  <td colSpan={4} className="border-t border-line px-2.5 py-3 text-ink-3">
                    Проходов за день нет
                  </td>
                </tr>
              )}
              {calc.passes.map((p) => (
                <tr
                  key={`${p.time}-${p.device}`}
                  className={`border-t border-line align-top ${p.mark === 'used' ? 'text-ink' : 'text-ink-3'}`}
                >
                  <td className="px-2.5 py-1.5 tabular-nums">{p.time}</td>
                  <td className="px-2.5 py-1.5 break-words">{p.device}</td>
                  <td className="px-2.5 py-1.5">
                    {p.direction === 'IN' ? 'вход' : p.direction === 'OUT' ? 'выход' : '—'}
                  </td>
                  <td className="px-2.5 py-1.5">
                    {p.mark === 'used' ? <Badge tone="ok">{MARK[p.mark]}</Badge> : MARK[p.mark]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section aria-label="Как посчитано">
        <h3 className="mb-2 text-[14px] font-semibold text-ink">Как посчитано</h3>
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          <div className="rounded-control border border-line bg-subtle px-4 py-3">
            <p className="text-[12px] text-ink-3">По СКУД</p>
            <p
              className={`mt-0.5 text-[18px] font-semibold tabular-nums ${calc.hours == null && PROBLEM.has(calc.state) ? 'text-bad' : 'text-ink'}`}
            >
              {calc.hours != null ? `${hours(calc.hours)} ч` : STATE_LABEL[calc.state] || 'нет данных'}
            </p>
            <p className="mt-0.5 text-[12px] text-ink-3">
              первый вход — последний выход · правила {calc.rulesVersion}
            </p>
          </div>
          <div
            className={`rounded-control border px-4 py-3 ${cell.manualHours != null ? 'border-warn/25 bg-edit' : 'border-line bg-subtle'}`}
          >
            <p className="text-[12px] text-ink-3">В табеле</p>
            <p
              className={`mt-0.5 text-[18px] font-semibold tabular-nums ${cell.finalHours == null && !cell.absenceCode && PROBLEM.has(cell.machineState) ? 'text-bad' : 'text-ink'}`}
            >
              {final}
            </p>
            <p className="mt-0.5 truncate text-[12px] text-ink-2">
              {cell.manualHours != null
                ? `✎ ${cell.manualComment ?? ''}`
                : cell.absenceCode
                  ? 'код отсутствия'
                  : 'как по СКУД'}
              {cell.lunch ? ' · работал в обед' : ''}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

/** Ошибка загрузки расчёта дня: текст по коду и «Код ошибки». */
function CalcError({ error }: { error: unknown }) {
  const e = error instanceof ApiError ? error : null;
  const text = errorText(error, 'Не удалось загрузить');
  return (
    <Callout tone="bad" icon={CircleAlert}>
      <p>Расчёт дня не загрузился: {text}.</p>
      {e?.requestId && (
        <p className="mt-0.5 text-[12px] text-ink-2">
          Код ошибки: <span className="font-mono select-all">{e.requestId}</span>
        </p>
      )}
    </Callout>
  );
}

/** Переключатель «Работал в обед» (дизайн-система, Choice `t-toggle`): применяется сразу. */
const TOGGLE =
  "relative h-[18px] w-[30px] shrink-0 cursor-pointer appearance-none rounded-full bg-line-strong transition-colors duration-150 checked:bg-accent disabled:cursor-not-allowed disabled:opacity-60 after:absolute after:top-[2px] after:left-[2px] after:h-[14px] after:w-[14px] after:rounded-full after:bg-surface after:shadow-card after:transition-transform after:duration-200 after:content-[''] checked:after:translate-x-3";

/**
 * Строка настройки (дизайн-система, SettingsRow): что и зачем слева, переключатель справа. С переключателем строка —
 * `<label>`: нажатие на всю строку переключает, цель нажатия — вся строка, не ниже 40 px (T103). С сегментами — `<div>`:
 * подпись не должна нажимать первый сегмент.
 */
function SettingRow({
  title,
  note,
  children,
  label = false,
}: { title: string; note: string; children: ReactNode; label?: boolean }) {
  const Row = label ? 'label' : 'div';
  return (
    <Row
      className={`flex min-h-10 items-center justify-between gap-4 border-t border-line py-2.5 first:border-t-0 ${label ? 'cursor-pointer' : 'max-sm:flex-col max-sm:items-start max-sm:gap-2'}`}
    >
      <span className="min-w-0">
        <span className="block text-[14px] font-medium text-ink">{title}</span>
        <span className="block text-[12.5px] text-ink-2">{note}</span>
      </span>
      {children}
    </Row>
  );
}

export interface DayCardProps {
  open: boolean;
  /** ✕, Esc, «Закрыть». Защиту несохранённого добавляет страница: `onClose={() => guard(close)}`. */
  onClose: () => void;
  timesheetId: number;
  /** Название табеля (бригада) — в крошке и подзаголовке. */
  timesheetName: string;
  personId: string;
  displayName: string;
  cell: Cell | null;
  /** «выходной», «праздник» или `null`. */
  dayOff: string | null;
  tzOffset: string;
  actions: DayActions;
  /** Формат часов правил объекта; `null` — табель без движка (часы 001). */
  hoursFormat: HoursFormat | null;
  onCopyLink: () => void;
  onHowItWorks: () => void;
  /** Куда вернуть фокус при закрытии: ячейка дня. */
  returnFocus?: () => HTMLElement | null | undefined;
  /** Вопросы дня (003, US2): из списка вопросов табеля; нет — из расчёта дня (`GET …/calc`). */
  questions?: readonly Question[];
  /** Какой вопрос дня открыть (нажали на него в списке); нет — первый, что требует действия. */
  focusQuestion?: number | null;
  /** Бригадир отвечает: своя бригада, «Черновик» и «Возвращён». */
  canAnswer?: boolean;
  /** Кадры принимают ответ или просят уточнить: «Завершён». */
  canReview?: boolean;
  /**
   * Роль: кадрам без ответа бригадира — «Бригадир ещё не ответил»; «Передать день агенту» в меню «…» (005, FR-064) —
   * по роли и профилю `/api/me` этой роли. Нет — роль интерфейса.
   */
  role?: AppRole;
  /** Очередь дней с вопросами: «3 из 9», ‹ ›, «Сохранить и дальше». */
  nav?: DayNav | null;
  /** «Назад» в шапке: карточка открыта из списка вопросов. */
  onBack?: () => void;
  /** Ответ записан: страница обновляет вопросы и перечитывает табель. */
  onAnswered?: (res: AnswerResponse) => void;
  /** Решение кадров записано. */
  onReviewed?: (q: Question) => void;
  /** Вопрос снят или изменился: текущие вопросы из `409 STALE_STATE`; страница перечитывает данные. */
  onStale?: (current: Question[]) => void;
  /** «Завершить табель» в подвале, когда вопросов без ответа больше нет. */
  onComplete?: () => void;
  /**
   * «Отменить» в тосте после ответа, правки и отмены правки (003, FR-038): кнопка по ответу сервера (`undoActionId`);
   * нет — тост без кнопки.
   */
  undo?: (res: unknown) => ToastAction | undefined;
}

/** Очередь дней с вопросами в шапке карточки (FR-025). */
export interface DayNav {
  /** Место дня в очереди с нуля; день не в очереди (ответ уже дан) — `null`. */
  index: number | null;
  /** Дней в очереди. */
  total: number;
  prev: () => void;
  next: () => void;
  /** Открыто «Ответить на вопросы»: после ответа — следующий вопрос дня или следующий день. */
  queue: boolean;
  /** В очереди есть другой день, кроме этого. */
  hasOther: boolean;
  /** Кадры: очередь — ответы на проверку. */
  review?: boolean;
}

/**
 * Карточка дня в правой панели: полоса состояния, заголовок, свойства, проходы, «Как посчитано», вопросы дня, правка
 * часов, «Отметки», «Подробно для разработчика», история. Полоса состояния — сначала состояние вопроса, без вопросов —
 * документ, правка, «на объекте». Подвал — по состоянию: правка открыта — «Отмена» · «Сохранить правку»; ответ на
 * вопрос — «Не сохранено» · «Сохранить ответ» / «Сохранить и дальше»; кадры, ответ без решения — «Уточнить» ·
 * «Принято»; иначе — «Изменить часы» · «Отменить правку» · «Следующий без ответа»; без действий — «Закрыть».
 */
export function DayCard({
  open,
  onClose,
  timesheetId,
  timesheetName,
  personId,
  displayName,
  cell,
  dayOff,
  tzOffset,
  actions,
  hoursFormat,
  onCopyLink,
  onHowItWorks,
  returnFocus,
  questions,
  focusQuestion = null,
  canAnswer = false,
  canReview = false,
  role,
  nav = null,
  onBack,
  onAnswered,
  onReviewed,
  onStale,
  onComplete,
  undo,
}: DayCardProps) {
  const { showInfo } = useToast();
  const { guard } = useUnsavedGuard();
  const date = cell?.date ?? '';
  // «Передать день агенту» (005, FR-064): пункт меню «…» и окно со ссылкой.
  const agentLink = useDayAgentLink({
    role,
    personId,
    date,
    open,
    tzOffset,
    subtitle: date ? `${displayName} · ${weekday(date)}, ${ruDate(date)}` : displayName,
  });
  // «Спросить агента об этом дне» (006, FR-052): там же, где «Передать день агенту», если есть помощник. Новый разговор
  // в правой панели «Помощник» с полем, заполненным ссылкой и вопросом; сообщение не уходит само.
  const assistant = useAssistant();
  const calc = useQuery({
    queryKey: ['calc', timesheetId, personId, date, cell?.manualHours, cell?.lunch, cell?.absenceCode],
    queryFn: () => api.calc(timesheetId, personId, date),
    enabled: open && Boolean(cell),
  });

  // Вопросы дня: из списка табеля (страница), иначе из расчёта дня. Открыт первый, что требует действия роли.
  const dayKeyStr = `${personId}|${date}`;
  const dayQs = (questions ?? calc.data?.questions ?? [])
    .filter((q) => q.personId === personId && q.date === date)
    .sort(compareQuestions);
  const pick = (q: Question) => (canReview ? toReview(q) : isUnanswered(q));
  const [sel, setSel] = useState<{ day: string; id: number | null; editing: number | null }>({
    day: '',
    id: null,
    editing: null,
  });
  const selHere = sel.day === dayKeyStr ? sel : { day: dayKeyStr, id: focusQuestion, editing: null };
  const active =
    dayQs.find((q) => q.id === selHere.id) ??
    dayQs.find(pick) ??
    dayQs.find(isUnanswered) ??
    dayQs[0] ??
    null;
  const editingAnswer = active !== null && selHere.editing === active.id;
  const answerable = canAnswer && active !== null && (isUnanswered(active) || editingAnswer);
  const draft = useAnswerDraft({
    timesheetId,
    question: active,
    displayName,
    format: hoursFormat,
    tzOffset,
    active: open && answerable,
    onStale,
    onSaved: (res, answer, { advance }) => {
      showInfo(answer.option === 'later' ? 'Отложено: «Уточню позже»' : 'Ответ сохранён', {
        action: undo?.(res),
      });
      onAnswered?.(res);
      const saved = new Set(res.questions.map((q) => q.id));
      const rest = dayQs.filter((q) => !saved.has(q.id) && isUnanswered(q));
      setSel({ day: dayKeyStr, id: rest[0]?.id ?? null, editing: null });
      if (advance && rest.length === 0 && nav?.queue) nav.next();
    },
  });
  // Кадры: «Принято» и «Уточнить» по ответу без решения, «Уточнить» — и по принятому (`accepted → clarify`, T111).
  const review = useReview({
    timesheetId,
    question: canReview && active && reviewable(active) ? active : null,
    displayName,
    onReviewed,
    onStale,
  });
  const restInDay = active ? dayQs.filter((q) => q.id !== active.id && isUnanswered(q)) : [];
  const saveLabel =
    nav?.queue && (restInDay.length > 0 || nav.hasOther) ? 'Сохранить и дальше' : 'Сохранить ответ';
  const keys = useAnswerKeys({
    enabled: open && Boolean(draft?.active),
    options: active ? answerOptions(active) : [],
    onPick: (o) => draft?.setOption(o),
    onSave: () => {
      if (draft?.canSave) void draft.submit().catch(() => {});
    },
  });

  // Новый день — карточка сверху; в очереди «Ответить на вопросы» — сразу у вопроса (когда расчёт дня загружен и
  // всё, что выше вопроса, уже на месте). Один раз на день.
  const questionsRef = useRef<HTMLElement>(null);
  const scrolledFor = useRef<string | null>(null);
  const loadedDay = Boolean(calc.data) || Boolean(calc.error);
  const inQueue = Boolean(nav?.queue);
  useEffect(() => {
    if (!open) {
      scrolledFor.current = null;
      return;
    }
    if (!loadedDay || scrolledFor.current === dayKeyStr) return;
    scrolledFor.current = dayKeyStr;
    const q = questionsRef.current;
    const body = (q?.closest('[data-panel-body]') ?? null) as HTMLElement | null;
    if (inQueue && q && body) {
      body.scrollTop += q.getBoundingClientRect().top - body.getBoundingClientRect().top - 12;
    } else if (body) {
      body.scrollTop = 0;
    }
  }, [open, loadedDay, dayKeyStr, inQueue]);
  const edit = useCellEdit({
    key: `${personId}|${date}`,
    cell,
    displayName,
    format: hoursFormat,
    save: actions.edit,
    screen: SCREEN,
    undo,
  });
  // Панель закрыта — форма правки закрыта: тот же день открывается снова с карточки, а не с формы (T089).
  const closeForm = useRef(edit.cancel);
  closeForm.current = edit.cancel;
  useEffect(() => {
    if (!open) closeForm.current();
  }, [open]);
  const [pending, setPending] = useState<string | null>(null);
  /** Сохранить сразу; `done` — тост, `undoable` — с «Отменить» по ответу сервера. */
  const now = (what: string, fn: () => Promise<unknown>, done?: string, undoable = false) => {
    setPending(what);
    fn()
      .then((res) => done && showInfo(done, { action: undoable ? undo?.(res) : undefined }))
      .catch(() => {}) // ошибку показывает страница: тост с «Повторить»
      .finally(() => setPending(null));
  };

  const data = calc.data;
  const engine = data?.engine?.calculated ? data.engine : null;
  const history = data?.history ?? [];
  const doc = cell ? documentOf(cell, engine) : null;
  // Пересчёт по новым правилам (004, US3): «правка устарела» ячейки и пометки подписанного дня.
  const stale = staleEditOf(cell);
  const signedNotes = signedNotesOf(data);

  let status: ReactNode = null;
  if (cell && active) {
    const s = questionStrip(active, tzOffset);
    status = (
      <StatusStrip tone={s.tone} icon={s.icon}>
        {s.content}
      </StatusStrip>
    );
  } else if (cell && doc) {
    status = (
      <StatusStrip tone="neutral" icon={Info}>
        <b className="font-semibold">
          Документ на весь день: {doc.code} — {ABSENCE_NAME[doc.code] ?? doc.code}
        </b>
        {doc.external ? ' · из другого табеля' : ''}
      </StatusStrip>
    );
  } else if (cell && cell.manualHours != null) {
    const last = lastEditOf(history, timesheetId);
    status = (
      <StatusStrip tone="warn" icon={PenLine}>
        <b className="font-semibold">Изменено вручную</b>
        {last ? ` ${stamp(last.ts, tzOffset)}` : ''}
        {last?.author ? ` · ${last.author}` : ''}
      </StatusStrip>
    );
  } else if (cell?.machineState === 'on_site') {
    status = (
      <StatusStrip tone="neutral" icon={Clock}>
        <b className="font-semibold">На объекте</b> — день ещё идёт, часы появятся после выхода
      </StatusStrip>
    );
  }

  // Предупреждения: потерянный проход — конкретно, со временем из расчёта, когда расчёт загружен.
  // Вопрос «потерян проход» заменяет предупреждение движка о том же: в карточке — только вопрос.
  const lostAsked = dayQs.some((x) => typeKind(x.type) === 'потерян_проход');
  const lost = engine && !lostAsked ? engine.day.lost : [];
  // Вопросы дня, снятые пересчётом после ответа (T109): расхождение «по проходам — по ответу» и отрезок ответа на полосе.
  // Только пока ответ бригадира в табеле (T129; FR-020): пометка исчезла (`flag_gone`), а правка бригадира ещё в
  // ячейке и документа дня нет. Снят кодом отсутствия, правку отменили или заменили кадры — сравнивать не с чем.
  const answerInTable = Boolean(cell && cell.manualHours != null && cell.manualByRole !== 'hr' && !doc);
  const removedQs = answerInTable
    ? (data?.removedQuestions ?? []).filter(
        (q) => q.personId === personId && q.date === date && q.removedReason === 'flag_gone',
      )
    : [];
  const removedNotes = removedQs.flatMap((q) => {
    const text = removedNote(q, { format: hoursFormat, tzOffset, date });
    return text ? [{ id: q.id, text }] : [];
  });
  const removedTime = removedQs.find((q) => q.answer?.option === 'time') ?? null;
  const notes = cell
    ? [
        ...lost.map((t) => `${t} Подпись это не блокирует.`),
        ...(cell.engineNotes ?? [])
          .filter((n) => (lost.length === 0 && !lostAsked) || !n.startsWith('потерян проход'))
          .map((n) => `${engineNote(n)}. Подробности — в расчёте ниже; подпись это не блокирует.`),
      ]
    : [];

  const canEditHours = Boolean(actions.edit) && !doc;
  const canCancel = Boolean(actions.cancelEdit) && cell?.manualHours != null;
  const readOnly = !actions.edit && !actions.cancelEdit && !actions.lunch && !actions.absence;
  // Код из табеля другой бригады меняют там, где его поставили: здесь — только документ дня с источником.
  const absenceHere = Boolean(actions.absence) && !cell?.absenceOther;
  const marks = !edit.open && cell && ((actions.lunch && !doc) || absenceHere);

  const editHoursBtn = canEditHours && (
    <Button icon={PenLine} disabled={pending !== null} onClick={edit.start}>
      Изменить часы
    </Button>
  );
  const canChangeAnswer = canAnswer && active !== null && isAnswered(active) && !editingAnswer;
  // Что дальше, когда здесь делать нечего: следующий день очереди или «Завершить табель».
  const tail =
    nav && nav.total > 0 && (nav.index === null || nav.hasOther) ? (
      <Button variant="primary" icon={ChevronRight} onClick={nav.next}>
        {nav.review ? 'Следующий ответ' : 'Следующий без ответа'}
      </Button>
    ) : canAnswer && onComplete && dayQs.length > 0 && (!nav || nav.total === 0) ? (
      <Button variant="primary" icon={Send} onClick={onComplete}>
        Завершить табель
      </Button>
    ) : null;

  let footer: ReactNode;
  if (edit.open) footer = <CellEditFooter edit={edit} />;
  else if (draft?.active)
    footer = (
      <AnswerFooter
        draft={draft}
        label={saveLabel}
        keys={keys}
        onCancelEdit={
          editingAnswer
            ? () => {
                draft.reset();
                setSel({ ...selHere, editing: null });
              }
            : undefined
        }
      />
    );
  else if (canReview && active && reviewable(active))
    footer = <ReviewFooter review={review} extra={editHoursBtn} canAccept={toReview(active)} />;
  else
    footer = (
      <>
        {editHoursBtn}
        {canCancel && cell && (
          <Button
            variant="ghost"
            icon={RotateCcw}
            disabled={pending !== null}
            onClick={() => {
              const base = baseOf(cell);
              now('cancel', () => actions.cancelEdit?.(base) ?? Promise.resolve(), 'Правка отменена', true);
            }}
          >
            Отменить правку
          </Button>
        )}
        {canChangeAnswer && active && (
          <Button
            icon={PenLine}
            onClick={() => setSel({ day: dayKeyStr, id: active.id, editing: active.id })}
          >
            Изменить ответ
          </Button>
        )}
        {/* Действий нет — «Закрыть» в подвале; есть — закрывают ✕ и Esc, подвал не переносится на телефоне. */}
        {!canEditHours && !canCancel && !canChangeAnswer && !tail && readOnly && (
          <span className="text-[12.5px] text-ink-3">Изменения недоступны в этом статусе</span>
        )}
        <span className="flex-1" aria-hidden />
        {tail ??
          (!canEditHours && !canCancel && !canChangeAnswer && <Button onClick={onClose}>Закрыть</Button>)}
      </>
    );

  const position = nav
    ? nav.total === 0
      ? nav.review
        ? 'ответов нет'
        : 'все отвечены'
      : `${nav.index === null ? '—' : nav.index + 1} из ${nav.total}`
    : null;
  const navButtons = nav && (
    <span className="mr-1 flex items-center gap-0.5">
      <span
        className="px-1 text-[12.5px] whitespace-nowrap text-ink-3 tabular-nums"
        title={nav.review ? 'Дней с ответами на проверку' : 'Дней с вопросами без ответа'}
      >
        {position}
      </span>
      <IconButton
        icon={ChevronLeft}
        label="Предыдущий день"
        title="Предыдущий день с вопросом"
        disabled={nav.total === 0}
        onClick={nav.prev}
      />
      <IconButton
        icon={ChevronRight}
        label="Следующий день"
        title="Следующий день с вопросом"
        disabled={nav.total === 0}
        onClick={nav.next}
      />
    </span>
  );

  const menu = (
    <Dropdown
      align="end"
      width="w-64"
      trigger={({ open: menuOpen, toggle }) => (
        <IconButton
          icon={MoreHorizontal}
          label="Ещё"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={toggle}
        />
      )}
    >
      {(close) => (
        <>
          <MenuItem
            icon={Link2}
            onClick={() => {
              close();
              onCopyLink();
            }}
          >
            Скопировать ссылку на день
          </MenuItem>
          {/* Выборка из одного дня для ИИ-агента (005, FR-064): только при условиях прав и разрешения объекта. */}
          {agentLink.allowed && (
            <MenuItem
              icon={Bot}
              onClick={() => {
                close();
                agentLink.start();
              }}
            >
              {AGENT_LINK_LABEL.menu}
            </MenuItem>
          )}
          {agentLink.allowed && assistant && date && (
            <MenuItem
              icon={MessagesSquare}
              onClick={() => {
                close();
                // Одна правая панель: карточка дня закрывается, открывается «Помощник».
                assistant.askAboutDay({ date, personId });
                onClose();
              }}
            >
              {ASSISTANT_LABEL.askDay}
            </MenuItem>
          )}
          <MenuItem
            icon={BookOpen}
            onClick={() => {
              close();
              onHowItWorks();
            }}
          >
            Как считаются часы
          </MenuItem>
        </>
      )}
    </Dropdown>
  );

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      label="Карточка дня"
      crumb={timesheetName}
      title="Карточка дня"
      icon={CalendarDays}
      onBack={onBack}
      actions={
        <>
          {navButtons}
          {menu}
        </>
      }
      status={status}
      footer={footer}
      returnFocus={returnFocus}
    >
      {!cell ? (
        <p className="text-[13px] text-ink-3">Этого дня нет в табеле.</p>
      ) : (
        <>
          <div>
            <h2 className="text-[19px] leading-[1.3] font-semibold tracking-[-0.015em] text-ink break-words">
              {displayName}
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-2">
              {[`${weekday(date)}, ${ruDate(date)}`, `бригада «${timesheetName}»`, dayOff]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          {notes.map((note) => (
            <Callout key={note} tone="warn" icon={TriangleAlert}>
              {note}
            </Callout>
          ))}
          {removedNotes.map((n) => (
            <Callout key={n.id} tone="warn" icon={MessageCircleQuestion}>
              <span data-removed-question>{n.text}</span>
            </Callout>
          ))}
          {stale && (
            <section aria-label={RECALC_LABEL.staleRegion}>
              <Callout tone="warn" icon={PenLine}>
                <p>
                  {sayRecalc.staleEdit(
                    stale.was === null ? null : hoursText(stale.was, hoursFormat),
                    cell.machineHours === null ? null : shownHours(cell.machineValue, cell.machineHours),
                  )}{' '}
                  {actions.editDecision && RECALC_TEXT.staleNote}
                </p>
                {actions.editDecision && (
                  <div className="mt-2 flex flex-wrap gap-2 max-sm:[&>button]:flex-auto">
                    <Button
                      disabled={pending !== null}
                      onClick={() =>
                        now(
                          'keep',
                          () => actions.editDecision?.('keep') ?? Promise.resolve(),
                          RECALC_TEXT.keep,
                        )
                      }
                    >
                      {RECALC_LABEL.keepEdit}
                    </Button>
                    <Button
                      disabled={pending !== null}
                      onClick={() =>
                        now(
                          'accept',
                          () => actions.editDecision?.('accept') ?? Promise.resolve(),
                          RECALC_TEXT.accept,
                        )
                      }
                    >
                      {RECALC_LABEL.acceptCalc}
                    </Button>
                  </div>
                )}
              </Callout>
            </section>
          )}
          {signedNotes.map((code) => (
            <Callout key={code} tone="neutral" icon={Info}>
              {SIGNED_NOTE_TEXT[code] ?? code}
            </Callout>
          ))}

          <Props
            cell={cell}
            doc={doc}
            engine={engine}
            history={history}
            timesheetId={timesheetId}
            tzOffset={tzOffset}
            format={hoursFormat}
            calc={data}
            questions={dayQs}
          />

          {calc.isLoading && <p className="text-[13px] text-ink-3">Загрузка расчёта…</p>}
          {calc.error && <CalcError error={calc.error} />}

          {data?.engine && !data.engine.calculated && <EngineNotCalculated />}
          {engine && (
            <>
              <DayPasses
                day={engine.day}
                date={date}
                stated={
                  statedOf(active, draft, date, tzOffset) ?? statedOf(removedTime, null, date, tzOffset)
                }
              />
              <section aria-label="Как посчитано" className="text-[13px]">
                <h3 className="mb-2 text-[14px] font-semibold text-ink">Как посчитано</h3>
                <EngineReceipt
                  engine={engine}
                  cell={cell}
                  history={history}
                  timesheetId={timesheetId}
                  tzOffset={tzOffset}
                  date={date}
                />
              </section>
            </>
          )}
          {data && !data.engine && <Calc001 calc={data} cell={cell} />}

          {dayQs.length > 0 && (
            <section
              ref={questionsRef}
              aria-label={dayQs.length > 1 ? 'Вопросы дня' : 'Вопрос'}
              className="flex scroll-mt-3 flex-col gap-2"
            >
              <h3 className="flex items-center gap-1.5 text-[14px] font-semibold text-ink">
                {dayQs.length > 1 ? 'Вопросы дня' : 'Вопрос'}
                {dayQs.length > 1 && (
                  <span className="text-[12px] font-normal text-ink-3 tabular-nums">
                    отвечено {dayQs.filter(isAnswered).length} из {dayQs.length}
                  </span>
                )}
              </h3>
              {dayQs.map((q) => (
                <QuestionBlock
                  key={q.id}
                  q={q}
                  expanded={q.id === active?.id}
                  single={dayQs.length === 1}
                  onExpand={() => guard(() => setSel({ day: dayKeyStr, id: q.id, editing: null }))}
                  draft={q.id === active?.id ? draft : null}
                  review={q.id === active?.id && canReview ? review : null}
                  readOnlyText={
                    canReview || role === 'hr'
                      ? 'Бригадир ещё не ответил на этот вопрос.'
                      : 'Табель не в работе — ответить нельзя.'
                  }
                  keys={keys}
                  format={hoursFormat}
                  tzOffset={tzOffset}
                />
              ))}
            </section>
          )}

          <CellEditForm edit={edit} />

          {marks && (
            <section aria-label="Отметки">
              <h3 className="mb-1 text-[14px] font-semibold text-ink">Отметки</h3>
              {actions.lunch && !doc && (
                <SettingRow
                  label
                  title="Работал в обед"
                  note="Сохраняется сразу. Часы пересчитаются по правилам объекта."
                >
                  <input
                    type="checkbox"
                    role="switch"
                    aria-checked={cell.lunch}
                    aria-label="Работал в обед"
                    className={TOGGLE}
                    checked={cell.lunch}
                    disabled={pending !== null}
                    onChange={(e) => {
                      const v = e.target.checked;
                      now('lunch', () => actions.lunch?.(v) ?? Promise.resolve());
                    }}
                  />
                </SettingRow>
              )}
              {absenceHere && (
                <SettingRow title="Код отсутствия" note="Вместо часов. Сохраняется сразу.">
                  <Segmented<AbsenceValue>
                    label="Код отсутствия"
                    touch="tablet"
                    value={cell.absenceCode ?? 'none'}
                    options={ABSENCE_OPTIONS.map((o) => ({ ...o, disabled: pending !== null }))}
                    onChange={(v) => {
                      if (v === (cell.absenceCode ?? 'none')) return;
                      now('absence', () => actions.absence?.(v === 'none' ? null : v) ?? Promise.resolve());
                    }}
                  />
                </SettingRow>
              )}
            </section>
          )}

          {engine && <EngineDayTrace engine={engine} tzOffset={tzOffset} />}
          {data && (
            <HistoryList
              items={history}
              recalcs={recalcsOf(data)}
              date={date}
              timesheetId={timesheetId}
              tzOffset={tzOffset}
              format={hoursFormat}
            />
          )}
        </>
      )}
      {/* Окно «Передать день агенту» — модальное (`<dialog>` поверх всего), закрывается вместе с карточкой. */}
      {agentLink.dialog}
    </SidePanel>
  );
}
